import React from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import {
  WINE_TRAINING_PAIRING_GUIDE_PATH,
  WINE_TRAINING_PILLAR_PATH,
} from "../lib/contentClusters";
import "../styles/homepage-redesign.css";

const startFreeHref = "https://app.speakyourmenu.com/signup";
const canonicalUrl = `https://speakyourmenu.com${WINE_TRAINING_PAIRING_GUIDE_PATH}`;
const pillarUrl = `https://speakyourmenu.com${WINE_TRAINING_PILLAR_PATH}`;
const pageTitle = "Wine Pairing Guide for Servers | Speak Your Menu";
const pageDescription = "A practical wine pairing guide for restaurant servers who need clearer recommendation language, better guest fit, and stronger bottle confidence.";

const guideSections = [
  {
    title: "Anchor pairings to guest intent",
    items: [
      "Teach staff to listen for what the guest wants first: lighter, richer, safer, celebratory, or adventurous.",
      "Connect each core wine to one or two common guest intents before adding technical detail.",
      "Make sure every by-the-glass recommendation has a simple explanation a guest can understand immediately.",
    ],
  },
  {
    title: "Use a short pairing language framework",
    items: [
      "Give staff a repeatable structure: what it tastes like, what dishes it fits, and why the pairing works.",
      "Avoid overloading recommendations with theory or jargon that slows the conversation down.",
      "Practice transitions from glass pours to bottle recommendations so upsell moments feel natural.",
    ],
  },
  {
    title: "Reinforce the highest-value moments",
    items: [
      "Focus first on your most-ordered dishes, most profitable pours, and the bottles managers want discussed more often.",
      "Revisit pairings that staff routinely avoid or explain poorly.",
      "Use pre-shift reinforcement to sharpen one pairing theme at a time instead of reteaching the whole list.",
    ],
  },
  {
    title: "Coach for confidence, not perfection",
    items: [
      "Look for hesitation, vagueness, and missed recommendation openings during service.",
      "Coach staff to sound clear and useful, not like sommeliers delivering a lecture.",
      "Repeat the same recommendation language until it feels natural across the full team.",
    ],
  },
];

export default function WineTrainingPairingGuidePage() {
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://speakyourmenu.com/" },
      { "@type": "ListItem", position: 2, name: "Restaurant Wine Training Guide", item: pillarUrl },
      { "@type": "ListItem", position: 3, name: "Wine Pairing Guide for Servers", item: canonicalUrl },
    ],
  };

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:site_name" content="Speak Your Menu" />
        <meta property="og:image" content="https://speakyourmenu.com/SpeakYourMenu_Logo.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <link rel="canonical" href={canonicalUrl} />
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>
      <Nav />
      <main id="main-content" tabIndex="-1" className="homepage-redesign resource-page-shell">
        <section className="homepage-section homepage-hero-section resource-hero-section">
          <div className="homepage-hero-bg" aria-hidden="true" />
          <div className="homepage-hero-glow homepage-hero-glow-left" aria-hidden="true" />
          <div className="homepage-hero-glow homepage-hero-glow-right" aria-hidden="true" />
          <div className="homepage-shell">
            <div className="resource-hero-copy">
              <span className="homepage-label-chip">Pairing Guide</span>
              <h1 className="homepage-hero-title resource-hero-title">A wine pairing guide your servers can actually use at the table.</h1>
              <p className="homepage-hero-subhead resource-hero-subhead">
                Use this guide to give your team simpler pairing language, clearer recommendation patterns, and more confidence moving from safe pours to stronger bottle conversations.
              </p>
              <div className="homepage-hero-actions resource-hero-actions">
                <a href={startFreeHref} className="homepage-primary-cta">Start free</a>
                <Link to={WINE_TRAINING_PILLAR_PATH} className="resource-secondary-link">Read the full wine-training guide</Link>
              </div>
            </div>
          </div>
        </section>

        <section className="homepage-section">
          <div className="homepage-shell resource-copy-shell">
            <span className="homepage-label-chip">Use it weekly</span>
            <h2 className="homepage-section-title resource-section-title">Wine pairing guide for servers</h2>
            <div className="resource-checklist-stack">
              {guideSections.map((section) => (
                <article key={section.title} className="homepage-surface-card resource-card">
                  <h3 className="homepage-card-title">{section.title}</h3>
                  <ul className="resource-bullet-list">
                    {section.items.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="homepage-section homepage-section-alt">
          <div className="homepage-shell resource-grid-two">
            <article className="homepage-surface-card resource-card">
              <span className="homepage-label-chip">Why it matters</span>
              <h2 className="homepage-section-title resource-section-title">Most pairing misses happen because the language is weak, not because the list is weak.</h2>
              <p className="homepage-section-body resource-section-body">
                Guests rarely need a lecture. They need a clear, confident recommendation that fits the dish and the moment. This guide gives managers a simpler framework for building that skill across the whole team.
              </p>
            </article>
            <article className="homepage-surface-card resource-card resource-card-accent">
              <span className="homepage-label-chip">Go deeper</span>
              <h2 className="homepage-card-title resource-aside-title">Need the full system behind the pairing guide?</h2>
              <p className="homepage-card-copy">The full wine-training guide explains how to structure beverage training beyond one-off lineup notes and bottle trivia.</p>
              <Link to={WINE_TRAINING_PILLAR_PATH} className="homepage-primary-cta resource-inline-cta">Read the guide</Link>
            </article>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}